import React, { useEffect, useState } from "react";
import { Card, Grid, Drawer } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import StatusChip from "../components/StatusChip";
import HealthBar from "../components/HealthBar";
import PlanBadge from "../components/PlanBadge";
import { useDispatch, useSelector } from "react-redux";
import { fetchTenants, setFilters } from "store/slices/tenantSlice";
import { connect } from "react-redux";
import { getCustomersListAction } from "Actions/getCustomerDataAction";
import AddTenantModal from "./AddTenantModal";

const STATUS_FILTERS = ["All", "Active", "Trial", "Suspended", "Provisioning", "Offboarding"];

const FILTER_COLORS = {
  All: { bg: "#E0F7FA", text: "#00ACC4", border: "#00ACC4" },
  Active: { bg: "#D5F5E3", text: "#0B6E4F", border: "#17A589" },
  Trial: { bg: "#EBF5FB", text: "#154360", border: "#2E86AB" },
  Suspended: { bg: "#FDEBD0", text: "#784212", border: "#E67E22" },
  Provisioning: { bg: "#F4ECF7", text: "#512E5F", border: "#8E44AD" },
  Offboarding: { bg: "#FADBD8", text: "#922B21", border: "#C0392B" },
};

function TenantList({ customersData, getCustomersListAction: fetchCustomers }) {
  const dispatch = useDispatch();
  const { list, filters } = useSelector((state) => state.tenants);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchTenants(filters));
    fetchCustomers();
  }, [dispatch, filters]);

  // Merge customers data into the tenant list
  const customersAsTenants = (customersData || []).map((c, i) => ({
    id: c._id || `customer-${i}`,
    name: c.customerName || "",
    status: (c.status || "").toLowerCase() === "enabled" ? "active" : "suspended",
    plan: c.productType || "standard",
    contactEmail: c.contactEmail || "",
    createdAt: c.createdAt || "",
    activeUsers: 0,
    healthScore: 0,
    tenantId: c.tenantId || `C-${String(i + 1).padStart(3, "0")}`,
    source: "legacy",
  }));

  const mergedList = [
    ...(list || []).map((t) => ({ ...t, source: "control-plane" })),
    ...customersAsTenants.filter(
      (c) => !(list || []).some((t) => t.tenantId === c.tenantId || t.name === c.name)
    ),
  ];

  // Local filter for fast UX
  const filtered = mergedList.filter((t) => {
    const matchSearch =
      (t.legalEntityName || t.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.tenantId || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.contactEmail || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "All" || (t.status || "").toLowerCase() === filterStatus.toLowerCase();
    return matchSearch && matchStatus;
  });

  const tenant = selectedTenant ? mergedList.find((t) => t.id === selectedTenant) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3} sx={{ display: "flex", gap: 3, overflow: "hidden" }}>
        {/* Main Content */}
        <MDBox flex={1} minWidth={0}>
          {/* Page Header */}
          <MDBox mb={2.5}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
              <span style={{ color: "#BDC3C7" }}>›</span>
              <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Tenants</span>
            </MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="space-between">
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Tenant Management</h1>
              <button
                onClick={() => setAddModalOpen(true)}
                style={{
                  background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + New Tenant
              </button>
            </MDBox>
          </MDBox>

          {/* Filters */}
          <MDBox display="flex" gap={1} mb={2} alignItems="center" flexWrap="wrap">
            <span style={{ fontSize: 12, color: "#5D6D7E", fontWeight: 500 }}>Status:</span>
            {STATUS_FILTERS.map((s) => {
              const isActive = filterStatus === s;
              const colors = FILTER_COLORS[s] || FILTER_COLORS.All;
              return (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    border: `1px solid ${isActive ? colors.border : "#E8EDF2"}`,
                    background: isActive ? colors.bg : "#fff",
                    color: isActive ? colors.text : "#5D6D7E",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {s}
                </button>
              );
            })}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#5D6D7E" }}>
              {filtered.length} tenants
            </span>
          </MDBox>

          {/* Table */}
          <div style={{ background: "#fff", border: "1px solid #E8EDF2", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "26%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}>
                  {["Tenant", "Plan", "Status", "Health", "MRR", "Seats", "Region", ""].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        color: "#5D6D7E",
                        fontWeight: 600,
                        fontSize: 13,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelectedTenant(t.id === selectedTenant ? null : t.id)}
                    style={{
                      borderBottom: "1px solid #F0F3F6",
                      cursor: "pointer",
                      background: selectedTenant === t.id ? "#D4E6F1" : i % 2 === 0 ? "#fff" : "#FAFBFC",
                      transition: "background 0.1s",
                    }}
                  >
                    <td style={{ padding: "11px 14px", overflow: "hidden" }}>
                      <div style={{ fontWeight: 600, color: "#1E3A5F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.legalEntityName || t.name}</div>
                      <div style={{ fontSize: 11, color: "#5D6D7E", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {t.contactEmail || "—"} · <span style={{ fontFamily: "monospace", color: "#00ACC4" }}>{t.tenantId}</span>
                        {t.tenantId && (
                          <span
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(t.tenantId); }}
                            className="material-icons-outlined"
                            style={{ cursor: "pointer", color: "#BDC3C7", fontSize: 12, marginLeft: 4, verticalAlign: "middle" }}
                            title="Copy ID"
                          >
                            content_copy
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <PlanBadge plan={t.plan} />
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <StatusChip status={t.status} />
                    </td>
                    <td style={{ padding: "11px 14px", minWidth: 120 }}>
                      <HealthBar score={t.healthScore || 0} />
                    </td>
                    <td style={{ padding: "11px 14px", fontWeight: 600, color: (t.mrr || 0) > 0 ? "#17A589" : "#BDC3C7" }}>
                      {(t.mrr || 0) > 0 ? `$${(t.mrr || 0).toLocaleString()}` : "—"}
                    </td>
                    <td style={{ padding: "11px 14px", color: "#5D6D7E", fontSize: 12 }}>
                      {t.activeUsers || 0}
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <span style={{ background: "#F0F3F6", color: "#5D6D7E", fontSize: 11, padding: "2px 8px", borderRadius: 6 }}>
                        {t.region || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      <button
                        style={{
                          padding: "6px 16px",
                          borderRadius: 6,
                          border: "1px solid #E8EDF2",
                          background: "#fff",
                          fontSize: 13,
                          cursor: "pointer",
                          color: "#00ACC4",
                          fontWeight: 600,
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #F0F3F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "#5D6D7E" }}>Showing {filtered.length} of {mergedList.length} tenants</span>
            </div>
          </div>
        </MDBox>

        {/* Right Drawer — Tenant Detail Panel */}
        {tenant && (
          <MDBox
            sx={{
              width: 340,
              flexShrink: 0,
              background: "#fff",
              border: "1px solid #E8EDF2",
              borderRadius: "12px",
              overflow: "auto",
              maxHeight: "calc(100vh - 120px)",
              animation: "slideIn 0.2s ease",
              "@keyframes slideIn": {
                from: { opacity: 0, transform: "translateX(20px)" },
                to: { opacity: 1, transform: "translateX(0)" },
              },
            }}
          >
            {/* Panel Header */}
            <MDBox sx={{ background: "#1E3A5F", p: 2.5, position: "sticky", top: 0, zIndex: 1 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDBox>
                  <MDTypography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{tenant.legalEntityName || tenant.name}</MDTypography>
                  <MDTypography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, mt: 0.3 }}>{tenant.contactEmail}</MDTypography>
                  <MDTypography sx={{ fontFamily: "monospace", color: "#7CBBE8", fontSize: 11, mt: 0.3 }}>{tenant.tenantId}</MDTypography>
                </MDBox>
                <button
                  onClick={() => setSelectedTenant(null)}
                  style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", cursor: "pointer", borderRadius: 6, padding: "4px 8px", fontSize: 14 }}
                >
                  ✕
                </button>
              </MDBox>
              <MDBox display="flex" gap={1} mt={1.5}>
                <StatusChip status={tenant.status} />
                <PlanBadge plan={tenant.plan} />
              </MDBox>
            </MDBox>

            {/* Panel Body */}
            <MDBox p={2.5}>
              {/* Quick Stats Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                {[
                  { label: "MRR", value: (tenant.mrr || 0) > 0 ? `$${(tenant.mrr || 0).toLocaleString()}` : "Trial", color: "#17A589" },
                  { label: "Health Score", value: (tenant.healthScore || 0) > 0 ? tenant.healthScore : "N/A", color: (tenant.healthScore || 0) >= 90 ? "#17A589" : (tenant.healthScore || 0) >= 70 ? "#E67E22" : "#C0392B" },
                  { label: "Active Users", value: tenant.activeUsers || 0, color: "#00ACC4" },
                  { label: "Region", value: tenant.region || "—", color: "#5D6D7E" },
                ].map((s, i) => (
                  <div key={i} style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 11, color: "#5D6D7E", marginBottom: 3 }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Health Bar */}
              {(tenant.healthScore || 0) > 0 && (
                <MDBox mb={2}>
                  <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>Health Score</MDTypography>
                  <HealthBar score={tenant.healthScore || 0} />
                </MDBox>
              )}

              {/* Feature Flags */}
              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 700, color: "#1E3A5F", mb: 1, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Feature Flags
                </MDTypography>
                {[
                  { flag: "SSO / SAML", on: ["enterprise", "business"].includes((tenant.plan || "").toLowerCase()) },
                  { flag: "API Rate Limiting", on: true },
                  { flag: "Custom Domain", on: (tenant.plan || "").toLowerCase() !== "starter" },
                  { flag: "Advanced Analytics", on: (tenant.plan || "").toLowerCase() === "enterprise" },
                ].map((f, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #F0F3F6" }}>
                    <span style={{ fontSize: 13, color: "#1C2833" }}>{f.flag}</span>
                    <div
                      style={{
                        width: 36,
                        height: 20,
                        borderRadius: 10,
                        background: f.on ? "#17A589" : "#E8EDF2",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          background: "#fff",
                          position: "absolute",
                          top: 3,
                          left: f.on ? 19 : 3,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </MDBox>

              {/* Actions */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { label: "Configure", color: "#00ACC4", bg: "#E0F7FA" },
                  { label: "View Detail", color: "#17A589", bg: "#D5F5E3" },
                  { label: (tenant.status || "").toLowerCase() === "active" ? "Suspend" : "Reactivate", color: (tenant.status || "").toLowerCase() === "active" ? "#E67E22" : "#17A589", bg: (tenant.status || "").toLowerCase() === "active" ? "#FDEBD0" : "#D5F5E3" },
                  { label: "Audit Log", color: "#5D6D7E", bg: "#F2F3F4" },
                ].map((a, i) => (
                  <button
                    key={i}
                    style={{ padding: 9, borderRadius: 8, border: "none", background: a.bg, color: a.color, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      {/* Add Tenant Modal */}
      <AddTenantModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </DashboardLayout>
  );
}

const mapStateToProps = (state) => ({
  customersData: state.getCustomersDataReducer.customersData,
});

const mapDispatchToProps = (dispatch) => ({
  getCustomersListAction: () => dispatch(getCustomersListAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TenantList);
